import React, { useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Badge, Button, CustomInput, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";

import { useAuth } from "../contexts/AuthContext";
import { PERMISSIONS } from "../services/roles";
import { analysesFor, aiStatus, buildAnalysisRequest, runAnalysis } from "../services/aiAnalysis";
import { loadAiConfig, loadWebSummaries, saveAiConfig } from "../services/aiAnalysisData";
import { hasAIDataProcessingConsent, subscribeAIDataProcessingConsent } from "../services/aiDataConsent";

const STATUS_COLOR = { disabled: "secondary", no_provider: "warning", no_consent: "info", ready: "success" };

export function useAiConfig() {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    let alive = true;
    loadAiConfig().then((c) => alive && setConfig(c));
    return () => {
      alive = false;
    };
  }, []);
  return [config, setConfig];
}

function useConsent() {
  const [consent, setConsent] = useState(() => hasAIDataProcessingConsent());
  useEffect(() => subscribeAIDataProcessingConsent((c) => setConsent(c.granted === true)), []);
  return consent;
}

function formatNumber(n, lang) {
  try {
    return new Intl.NumberFormat(lang).format(n);
  } catch {
    return String(n);
  }
}

function DatasetSummary({ id, summary, t, lang }) {
  if (!summary) return null;
  const breakdown = [
    summary.byStatus.length ? `${t("aiAnalysis.byStatus")}: ${summary.byStatus.map(([k, n]) => `${k} ${n}`).join(", ")}` : null,
    summary.byGroup.length ? `${t("aiAnalysis.byGroup")}: ${summary.byGroup.map(([k, n]) => `${k} ${n}`).join(", ")}` : null
  ].filter(Boolean);
  return (
    <tr>
      <th scope="row" className="text-wrap">{t(`aiAnalysis.datasets.${id}`)}</th>
      <td className="text-wrap">
        {summary.count === 0 ? (
          <span className="text-muted">{t("aiAnalysis.noData")}</span>
        ) : (
          <>
            <div>
              {t("aiAnalysis.records", { count: summary.count })} · {t("aiAnalysis.last30", { count: summary.last30 })}
            </div>
            {summary.from && <div className="text-muted small">{t("aiAnalysis.range", { from: summary.from, to: summary.to })}</div>}
            {Object.entries(summary.sums).map(([f, v]) => (
              <div key={f} className="small">
                {t(`aiAnalysis.fields.${f}`)}: {formatNumber(v, lang)}
              </div>
            ))}
            {breakdown.map((line) => (
              <div key={line} className="text-muted small">{line}</div>
            ))}
          </>
        )}
      </td>
    </tr>
  );
}

/** Analysis templates, data preview and the (gated) run button for one module. */
export function AiAnalysisContent({ moduleId, config }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth() || {};
  const consent = useConsent();
  const lang = i18n?.language || "tr";
  const analyses = analysesFor(moduleId);
  const [analysisId, setAnalysisId] = useState(analyses[0].id);
  const [data, setData] = useState({ summaries: {}, failed: [] });
  const [loading, setLoading] = useState(true);
  const [showPayload, setShowPayload] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const payloadId = useId();

  useEffect(() => {
    setAnalysisId(analysesFor(moduleId)[0].id);
    setResult(null);
  }, [moduleId]);

  const analysis = analyses.find((a) => a.id === analysisId) || analyses[0];
  const datasetKey = analysis.datasets.join(",");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    loadWebSummaries(datasetKey.split(","), user?.uid).then((res) => {
      if (!alive) return;
      setData(res);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [datasetKey, user?.uid]);

  const status = config ? aiStatus({ config, consent }) : "disabled";
  const request = useMemo(
    () => buildAnalysisRequest({ moduleId, analysisId: analysis.id, summaries: data.summaries, language: lang }),
    [moduleId, analysis.id, data.summaries, lang]
  );

  const analyze = async () => {
    setRunning(true);
    setError(null);
    try {
      setResult(await runAnalysis(request, { config, consent }));
    } catch (e) {
      setError(e?.code || "AI_ERROR");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fd-ai-analysis">
      <div className="d-flex align-items-center mb-2" style={{ gap: "0.5rem" }}>
        <Badge color={STATUS_COLOR[status]} pill>{t(`aiAnalysis.status.${status}`)}</Badge>
      </div>
      <p className="small mb-1">{t(`aiAnalysis.statusDesc.${status}`)}</p>
      {status === "no_consent" && (
        <p className="small mb-1">
          <Link to="/admin/settings/ai-data-processing">{t("aiAnalysis.status.no_consent")}</Link>
        </p>
      )}
      <p className="small text-muted">{t("aiAnalysis.privacy")}</p>

      <div role="group" aria-label={t("aiAnalysis.title")} className="mb-3">
        {analyses.map((a) => (
          <button
            key={a.id}
            type="button"
            className="fd-ai-card"
            aria-pressed={a.id === analysis.id}
            onClick={() => {
              setAnalysisId(a.id);
              setResult(null);
              setShowPayload(false);
            }}
          >
            <strong className="d-block">{t(`aiAnalysis.analyses.${a.id}.title`)}</strong>
            <small className="d-block">{t(`aiAnalysis.analyses.${a.id}.desc`)}</small>
            <small className="d-block mt-1">{a.datasets.map((d) => t(`aiAnalysis.datasets.${d}`)).join(" · ")}</small>
          </button>
        ))}
      </div>

      <h4 className="h6 text-uppercase text-muted">{t("aiAnalysis.dataPreview")}</h4>
      {loading ? (
        <p className="small text-muted" role="status">{t("aiAnalysis.loading")}</p>
      ) : (
        <>
          {data.failed.length > 0 && <p className="small text-warning">{t("aiAnalysis.loadError")}</p>}
          <Table size="sm" responsive className="mb-2">
            <tbody>
              {analysis.datasets.map((d) => (
                <DatasetSummary key={d} id={d} summary={data.summaries[d]} t={t} lang={lang} />
              ))}
            </tbody>
          </Table>
          <Button color="link" size="sm" className="px-0" aria-expanded={showPayload} aria-controls={payloadId} onClick={() => setShowPayload((v) => !v)}>
            {showPayload ? t("aiAnalysis.hidePayload") : t("aiAnalysis.showPayload")}
          </Button>
          {showPayload && <pre id={payloadId}>{JSON.stringify(request, null, 2)}</pre>}
        </>
      )}

      {error && <Alert color="warning" className="mt-2">{t(`aiAnalysis.status.${error === "AI_NO_CONSENT" ? "no_consent" : error === "AI_DISABLED" ? "disabled" : "no_provider"}`)}</Alert>}
      {result && (
        <div className="mt-3" aria-live="polite">
          <p>{result.summary}</p>
          <ul className="pl-3">
            {(result.findings || []).map((f, i) => (
              <li key={i}>
                <strong>{f.title}</strong> {f.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="d-flex align-items-center mt-3" style={{ gap: "0.75rem" }}>
        <Button color="primary" disabled={status !== "ready" || loading || running} onClick={analyze}>
          {t("aiAnalysis.analyze")}
        </Button>
        {status !== "ready" && <small className="text-muted">{t(`aiAnalysis.status.${status}`)}</small>}
      </div>
    </div>
  );
}

/** Full Admin switch for config/aiAnalysis. Off by default; a provider is connected separately. */
export function AiAdminSwitch({ config, onChange }) {
  const { t } = useTranslation();
  const { user, isFullAdmin } = useAuth() || {};
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  if (typeof isFullAdmin !== "function" || !isFullAdmin() || !config) return null;

  const toggle = async (enabled) => {
    setSaving(true);
    setMessage(null);
    try {
      onChange(await saveAiConfig({ enabled, provider: config.provider }, user?.uid));
      setMessage({ color: "success", text: t("aiAnalysis.admin.saved") });
    } catch {
      setMessage({ color: "danger", text: t("aiAnalysis.admin.saveError") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section aria-labelledby="fd-ai-admin-title" className="border rounded p-3 mb-4">
      <h3 id="fd-ai-admin-title" className="h5">{t("aiAnalysis.admin.title")}</h3>
      <CustomInput
        type="switch"
        id="fd-ai-enabled"
        label={t("aiAnalysis.admin.switch")}
        checked={config.enabled}
        disabled={saving}
        onChange={(e) => toggle(e.target.checked)}
      />
      <p className="small text-muted mt-2 mb-1">{t("aiAnalysis.admin.hint")}</p>
      <p className="small mb-0">
        {t("aiAnalysis.admin.provider")}: {config.provider || t("aiAnalysis.admin.providerNone")}
      </p>
      {message && <Alert color={message.color} className="mt-2 mb-0">{message.text}</Alert>}
    </section>
  );
}

/** Floating "AI analysis" button on every admin page; opens the module's analyses in a side panel. */
export function AiAnalysisLauncher({ moduleId }) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [config] = useAiConfig();
  if (typeof hasPermission === "function" && !hasPermission(PERMISSIONS.AI_ACCESS)) return null;
  const close = () => setOpen(false);

  return (
    <>
      <button type="button" className="fd-ai-launcher" onClick={() => setOpen(true)} aria-haspopup="dialog">
        <i className="ni ni-atom" aria-hidden="true" />
        {t("aiAnalysis.open")}
      </button>
      <Modal isOpen={open} toggle={close} modalClassName="fd-ai-panel" scrollable>
        <ModalHeader toggle={close}>
          {t("aiAnalysis.contextTitle", { module: t(`aiAnalysis.modules.${moduleId}`) })}
        </ModalHeader>
        <ModalBody>{open && <AiAnalysisContent moduleId={moduleId} config={config} />}</ModalBody>
        <ModalFooter>
          <Link to={`/admin/ai/analysis?module=${moduleId}`} onClick={close}>
            {t("aiAnalysis.openHub")}
          </Link>
        </ModalFooter>
      </Modal>
    </>
  );
}
